import { Star, Quote, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

// Google My Business reviews URL
const GOOGLE_REVIEWS_URL = "https://share.google/Jpvieeltu7ZlLeEMu";

const featuredReviews = [
  {
    name: "Montserrat",
    text: "El servicio prestado es excelente. Son un equipo amable, responsable, honesto y muy diligente. Sus precios son competitivos y realizan seguimiento constante al trabajo. Incluso me corrigieron fallas que dejaron otros proveedores. ¡Totalmente recomendados!"
  },
  {
    name: "Oscar Sarmiento",
    text: "Los recomiendo porque mantienen altos estándares de seguridad. Sus productos son de última generación y funcionan a la perfección. Felicitaciones y que sigan los éxitos."
  },
  {
    name: "San Miguel Arcángel",
    text: "Excelente servicio. Siempre están atentos a tus necesidades y te asesoran sobre lo que realmente requieres. Muy satisfecho."
  }
];

const gridReviews = [
  { name: "Iván Rivas", text: "100 % recomendable. La entrega fue muy rápida y el servicio impecable. ¡Gracias!" },
  { name: "Nelson Suárez", text: "Agradezco la atención al cliente y que me mantuvieran informado sobre el envío. Estoy muy satisfecho." },
  { name: "Carina Egui", text: "Excelente servicio; 100 % seguro y confiable. Recomendados." },
  { name: "Kendys Govea", text: "Excelente servicio, siempre atentos, eficientes y eficaces. Los recomiendo al 100 %." },
  { name: "Brandon Ruiz", text: "Les doy 10/10. Son un equipo responsable y con muy buena atención." },
  { name: "Damaris Vélez", text: "Siempre dispuestos a dar la mejor asesoría. Buenos precios y muy competitivos." }
];

const NewTestimonials = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      id="testimonios" 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-14 md:py-20 bg-muted/30 scroll-mt-20"
    >
      <div className="container px-4 sm:px-6">
        {/* Header */}
        <div className={cn(
          "text-center mb-10 sm:mb-16 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <span className="inline-block text-primary font-medium mb-3 uppercase tracking-wide text-sm">
            Testimonios
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
            Lo dicen nuestros clientes
          </h2>
          <a 
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 group/rating"
          >
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-secondary text-secondary" />
              ))}
            </div>
            <span className="text-sm sm:text-base text-muted-foreground group-hover/rating:text-foreground transition-colors">
              4.9/5 · +70 valoraciones · Desde 2011
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-primary font-medium opacity-0 group-hover/rating:opacity-100 transition-opacity">
              Ver en Google <ExternalLink className="w-3 h-3" />
            </span>
          </a>
        </div>

        {/* Google verification badge */}
        <div className={cn(
          "flex justify-center mb-10 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <a 
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border/50 rounded-full text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-all group"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Reseñas verificadas de Google
            <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>

        {/* Featured reviews */}
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-12 max-w-5xl mx-auto">
          {featuredReviews.map((review, index) => (
            <a
              key={index}
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "group relative bg-card p-5 sm:p-8 rounded-2xl border border-border/50 hover:border-primary/50 hover:-translate-y-2 hover:shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.3)] transition-all duration-500 overflow-hidden cursor-pointer",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: isVisible ? `${100 + index * 100}ms` : "0ms" }}
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:via-transparent group-hover:to-secondary/5 transition-all duration-500 pointer-events-none" />
              
              <Quote className="absolute top-6 right-6 w-8 h-8 text-primary/10 group-hover:text-primary/20 group-hover:scale-110 transition-all duration-300" />
              
              <div className="relative flex gap-1 text-secondary mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              
              <p className="relative text-foreground/80 mb-6 leading-relaxed">
                "{review.text}"
              </p>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <span className="text-primary font-semibold text-sm group-hover:text-primary-foreground transition-colors">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <span className="font-medium text-foreground">{review.name}</span>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
        </div>

        {/* Grid reviews */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-12">
          {gridReviews.map((review, index) => (
            <a
              key={index}
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "group bg-card/50 p-5 rounded-xl border border-border/30 hover:border-primary/40 hover:bg-card hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.2)] transition-all duration-300 cursor-pointer",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
              style={{ transitionDelay: isVisible ? `${300 + index * 50}ms` : "0ms" }}
            >
              <div className="flex gap-0.5 text-secondary mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-current" />
                ))}
              </div>
              <p className="text-sm text-foreground/70 group-hover:text-foreground/80 mb-2 transition-colors">"{review.text}"</p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-foreground">{review.name}</span>
                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
        </div>

        <div className={cn(
          "text-center transition-all duration-700 delay-500",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <Button variant="gold" size="lg" className="group" asChild>
            <Link to="/contacto">
              Quiero una propuesta
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NewTestimonials;
